import React from 'react'
import { createSelector } from 'reselect'
import { connect } from 'react-redux'
import { get, sortBy, values, debounce, floor, split } from 'lodash'
import DocumentTitle from "react-document-title"
import { Link, browserHistory } from 'react-router'

import {
  Glyphicon, Panel, Table,
  FormGroup, FormControl, ControlLabel,
  DropdownButton, InputGroup, MenuItem
} from 'react-bootstrap'

import {
  getFromQuery2, getReservations, getAllTrips, getAllActivities, getAllRoutes
} from '../selectors'
import { getText } from '../lib/text'
import { replaceQuery } from '../lib/AppTools.js'
import { updateReservation } from '../ducks/reservations.js'

import Reservation from './Reservation.jsx'
import SupplierPicker from './components/SupplierPicker'
import SortSelector from './components/SortSelector'
import Pager from '../components/Pager'

import IfAllowed from "../components/IfAllowed"

import "./Purchases.less"

// WARNING: duped in more files
const NO_SUPPLIER = "__NO_SUPPLIER__"
const WITH_SUPPLIER = "__WITH_SUPPLIER__"

const sortingMethods = {
  mod: { value: "mod", name: "Fecha de modificación ascendente", sort: "modification" },
  modd: { value: "modd", name: "Fecha de modificación descendente", sort: "modification" },
  con: { value: "con", name: "Concepto", sort: o => (o.name ? o.name.toLowerCase() : "") },
  sup: { value: "sup", name: "Proveedor", sort: "supplier" },
  dat: { value: "dat", name: "Fecha de reserva ascendente", sort: x => (x.repetitionDate ? x.repetitionDate[0] : x.date) },
  datd: { value: "datd", name: "Fecha de reserva descendente", sort: x => (x.repetitionDate ? x.repetitionDate[0] : x.date) },
  cli: { value: "cli", name: "Cliente", sort: "tripId" },
}

const searchFields = {
  name: "Nombre",
  title: "Nombre (ch)",
  notes: "Nota",
  receipt: "#Fact.",
  finalPrice: "Importe exacto",
  rangePrice: "Importe por rangos"
}

/**
 * Selectors
 */
// duped from TripsSelector.jsx, sorry
const match = (term, prop) => (
   prop && prop.toLowerCase().replace(/\s/g, '').indexOf(term) >= 0
)

const matchNumber = (term, prop) => (
  prop == term
)

// Purchases are Reservations in Res or Pag states
const getPurchasesList = createSelector(
  [
    getReservations,
    getFromQuery2("sortBy"),
    getFromQuery2("filterType"),
    getFromQuery2("filter"),
    getFromQuery2("searchTerm"),
    getFromQuery2("searchField"),
  ],
  (reservations, sortKey, filterType, filter, searchTerm, searchField) => {
    let cleanTerm = (searchField == 'finalPrice'
      ? parseInt(searchTerm)
      : (searchTerm || "").toLowerCase().replace(/\s/g, ''))
    
    const rangos = split(cleanTerm, '-') // pasar a int aqui el proximo dia **********************
    const menor = parseInt(rangos[0])
    const mayor = parseInt(rangos[1])  

    const filteredList = values(reservations)
      .filter(r => (r.status == "res" || r.status == "pag" || r.status == "wtf"))
      .filter(reservation => (
        !filter
        || (filterType != "supplier")
        || (filter == NO_SUPPLIER && !reservation[filterType])
        || (filter == WITH_SUPPLIER && reservation[filterType])
        || (filter == reservation[filterType])
      ))
      .filter(reservation => {
        if (searchField == 'finalPrice'){
          if(cleanTerm == 0) // to resolve !cleanTerm problem
            return matchNumber(cleanTerm, floor(reservation[searchField]))
          else return !cleanTerm || matchNumber(cleanTerm, floor(reservation[searchField]))
        }
        else if(searchField == 'rangePrice') {
          if(reservation.finalPrice >= menor && reservation.finalPrice <= mayor || !cleanTerm)
            return true
          else return false
        }
        else{
          return !cleanTerm || match(cleanTerm, reservation[searchField])
        }
      })
      const sort = sortingMethods[sortKey || "con"].sort
      let sortedList = []

    if(sortKey=="modd" || sortKey=="datd"){
      sortedList = sortBy(filteredList, sort).reverse()
    }
    else{
      sortedList = sortBy(filteredList, sort)
    }
    return sortedList
  }
)
/**
 * PurchasesPage component
 */

type Props = {
  Reservations: Object[],
  Trips: Object[],
  Activities: Object[],
  updateChanges: (string, Object) => void, // TODO
  query: {
    filterType: ?string,
    filter: ?string
  },
}

class PurchasesPage extends React.PureComponent
{
  constructor(props) {
    super(props)

    this.pageSizes = [ 100, 250, 500 ]

    this.state = {
      supplierFilter: null,
      loading: false,
      page: 0,
      pageSize: this.pageSizes[0],

      // this is both in state (the latest value)
      // and in the query string (a debounced version, actually used for search)
      searchTerm: get(this.props, "query.searchTerm", "")
    }

    this.boundOnReservationChanges = this._onReservationChanges.bind(this)

    this.debouncedUpdateSearch = debounce(() => {
      replaceQuery(browserHistory, this.props.query, { searchTerm: this.state.searchTerm })
    }, 500, { trailing: true })
  }

  props: Props

  _onReservationChanges(id, itemIgnored, changes) {
    this.props.updateReservation(id, changes)
  }

  render() {
    const { Trips, query } = this.props
    const numPages = Math.ceil(this.props.purchasesList.length / this.state.pageSize)

    return (
      <IfAllowed verb="read" collection="reservations" else={<h1>No disponible</h1>}>
        <div className="purchases-page" style={{ padding: 15, margin: "auto", maxWidth: 1250 }}>
          <DocumentTitle title="Compras" />
          <h3 style={{marginTop: 0}}>Compras</h3>

          <Link to="/private/reservationsreport">
            <Glyphicon glyph="share-alt" />
            Reporte de cambios en las reservas
          </Link>

          <Panel className="filters">
            <Panel.Body className="body">
              <SupplierPicker
                label={getText("Filtrar por proveedor:")}
                query={this.props.query}
                value={(query.filterType == "supplier" && query.filter) || ""}
                onChange={filter => replaceQuery(browserHistory, query, { filterType: "supplier", filter })}
                includeNegativeFilters
              />
              
              <SortSelector
                includeLabel
                query={this.props.query}
                sortingMethods={sortingMethods}
              />
              <FormGroup className="search">
                <ControlLabel>Búsqueda</ControlLabel>
                <InputGroup>
                  <FormControl
                    placeholder = {query.searchField == "rangePrice" ? "(Ej: 0 - 100)" : ""}
                    className="search-input"
                    value={this.state.searchTerm}
                    onChange={evt => {
                      this.setState({ searchTerm: evt.target.value })
                      this.debouncedUpdateSearch()
                    }}
                  />
                  <DropdownButton
                    title={searchFields[query.searchField]}
                    id="input-dropdown-addon"
                    componentClass={InputGroup.Button}
                  >
                    {
                      ["name", "title", "notes", "receipt", "finalPrice", "rangePrice"]
                        .map(searchField => (
                          <MenuItem
                            key={searchField}
                            onClick={() => replaceQuery(browserHistory, query, { searchField })}
                          >
                            {searchFields[searchField]}
                          </MenuItem>
                        ))
                    }
                  </DropdownButton>
                </InputGroup>
              </FormGroup>
            </Panel.Body>
          </Panel>

          {
            numPages > 0 && this.props.purchasesList.length > this.pageSizes[0] && (
              <Pager
                sizes={this.pageSizes}
                size={this.state.pageSize}
                numPages={numPages}
                page={this.state.page}
                onChange={page => this.setState({ page })}
                onSizeChange={pageSize => this.setState({ pageSize, page: 0 })}
                style={{margin: 10}}
              />
            )
          }

          <Table>
            <thead style={{display:"none"}}>
              <tr>
                {
                  ["Item", "Estado reserva", "Precio plataforma", "Base imponible", "Total", "Notas"]
                    .map(x => <th key={x}>{getText(x)}</th>)
                }
              </tr>
            </thead>
            <tbody>
              {
                this.state.loading && (
                  <div style={{ fontStyle: "" }}>
                    Loading...
                  </div>
                )
              }
              {
                this.props.purchasesList
                  .slice(
                    this.state.pageSize * this.state.page,
                    this.state.pageSize * (this.state.page + 1),
                  )
                  .map(r => (
                    <Reservation
                      includeTrip
                      key={r._id}
                      trip={Trips[r.tripId]}
                      reservation={r}
                      error={false}
                      onChange={this.boundOnReservationChanges}
                    />
                  ))
              }
            </tbody>
          </Table>

          {
            numPages > 0 && this.props.purchasesList.length > this.pageSizes[0] && (
              <Pager
                numPages={numPages}
                page={this.state.page}
                onChange={page => this.setState({ page })}
              />
            )
          }
        </div>
      </IfAllowed>
    )
  }
}

const mapStateToProps = (state, ownProps) => ({
  wholeState: state,

  Trips: getAllTrips(state, ownProps),
  Activities: getAllActivities(state, ownProps),
  Routes: getAllRoutes(state, ownProps),

  purchasesList: getPurchasesList(state, ownProps),

  query: {
    filterType:  getFromQuery2("filterType")(state, ownProps),
    filter:      getFromQuery2("filter")(state, ownProps),
    sortBy:      getFromQuery2("sortBy")(state, ownProps),
    searchTerm:  getFromQuery2("searchTerm")(state, ownProps),
    searchField: getFromQuery2("searchField")(state, ownProps) || "name",
  }
})

const mapDispatchToProps = dispatch => ({
  updateReservation: (id, changes) => dispatch(updateReservation(id, changes)),
})

export default connect(mapStateToProps, mapDispatchToProps)(PurchasesPage)
